import client from "./client";
import { getJobStatus } from "./vistaBatchExport";
import { JobStartResponse, JobStatus, JobStatusEnum } from "../interfaces/V2Interface";

const CSV_VOUCHER_IMPORT_PATH = "/admin/riceServices/csvVoucherPieceImport";

export class CsvVoucherPieceImportError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = "CsvVoucherPieceImportError";
    this.status = status;
  }
}

function getApiErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as { response?: { data?: { error?: string }; status?: number } }).response;
    if (response?.data?.error) {
      return response.data.error;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Import failed to start";
}

export async function startCsvVoucherPieceImport(file: File): Promise<JobStartResponse> {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await client.post(CSV_VOUCHER_IMPORT_PATH, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    if (!response.data?.jobID) {
      throw new CsvVoucherPieceImportError("Import failed to start");
    }

    return response.data as JobStartResponse;
  } catch (error: unknown) {
    const status =
      error && typeof error === "object" && "response" in error
        ? (error as { response?: { status?: number } }).response?.status
        : undefined;
    throw new CsvVoucherPieceImportError(getApiErrorMessage(error), status);
  }
}

export async function pollJobUntilTerminal(
  jobID: number,
  onProgress?: (jobStatus: JobStatus) => void,
  intervalMs = 3000,
  signal?: AbortSignal
): Promise<JobStatus> {
  while (true) {
    if (signal?.aborted) {
      throw new DOMException("Import cancelled", "AbortError");
    }

    const jobStatus = await getJobStatus(jobID);
    onProgress?.(jobStatus);

    if (
      jobStatus.status === JobStatusEnum.COMPLETED ||
      jobStatus.status === JobStatusEnum.FAILED
    ) {
      return jobStatus;
    }

    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(resolve, intervalMs);
      signal?.addEventListener(
        "abort",
        () => {
          clearTimeout(timeout);
          reject(new DOMException("Import cancelled", "AbortError"));
        },
        { once: true }
      );
    });
  }
}
